import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { WorkoutPlan, WorkoutRoutine, ActiveSessionInfo } from '@/lib/types';

async function fetchRoutinesAndSession(studentId: string) {
  const { data: routineData, error: rErr } = await supabase
    .from('workout_routines')
    .select('*')
    .eq('student_id', studentId)
    .order('start_date', { ascending: false });

  if (rErr || !routineData) return { routines: [], activeSession: null };

  const routineIds = routineData.map(r => r.id);
  const { data: planData } = routineIds.length > 0
    ? await supabase.from('workout_plans').select('*').in('routine_id', routineIds).order('created_at')
    : { data: [] as WorkoutPlan[] };

  const planIds = (planData ?? []).map(p => p.id);
  const lastPerformed: Record<string, string> = {};
  const sessionCount: Record<string, number> = {};
  if (planIds.length > 0) {
    const { data: sessions } = await supabase
      .from('workout_sessions')
      .select('plan_id, completed_at')
      .in('plan_id', planIds)
      .not('completed_at', 'is', null)
      .order('completed_at', { ascending: false });
    (sessions ?? []).forEach(s => {
      if (!lastPerformed[s.plan_id]) lastPerformed[s.plan_id] = s.completed_at;
      sessionCount[s.plan_id] = (sessionCount[s.plan_id] ?? 0) + 1;
    });
  }

  const routines: WorkoutRoutine[] = routineData.map(r => ({
    ...r,
    plans: (planData ?? [])
      .filter(p => p.routine_id === r.id)
      .map(p => ({ ...p, lastPerformedAt: lastPerformed[p.id] ?? null, sessionCount: sessionCount[p.id] ?? 0 })),
  }));

  const { data: activeSessions } = await supabase
    .from('workout_sessions')
    .select('id, plan_id')
    .eq('student_id', studentId)
    .is('completed_at', null)
    .order('created_at', { ascending: false })
    .limit(1);

  const raw = activeSessions?.[0] ?? null;
  if (!raw) return { routines, activeSession: null };

  const [{ count: logCount }, { count: totalCount }] = await Promise.all([
    supabase.from('session_logs').select('*', { count: 'exact', head: true }).eq('session_id', raw.id),
    supabase.from('plan_exercises').select('*', { count: 'exact', head: true }).eq('plan_id', raw.plan_id),
  ]);

  const activePlan = (planData ?? []).find(p => p.id === raw.plan_id);
  const activeSession: ActiveSessionInfo = {
    sessionId: raw.id,
    planId: raw.plan_id,
    planName: activePlan?.name ?? 'Treino',
    logCount: logCount ?? 0,
    totalCount: totalCount ?? 0,
  };

  return { routines, activeSession };
}

export function useWorkoutRoutines(studentId: string) {
  const queryClient = useQueryClient();

  const { data, isLoading: loading, refetch } = useQuery({
    queryKey: ['workout-routines', studentId],
    queryFn: () => fetchRoutinesAndSession(studentId),
    enabled: !!studentId,
  });

  const { mutateAsync: createRoutine } = useMutation({
    mutationFn: async (input: { name: string; personal_id: string; start_date?: string; end_date?: string }) => {
      const { data: row, error } = await supabase
        .from('workout_routines')
        .insert({ ...input, student_id: studentId })
        .select()
        .single();
      if (error) throw error;
      return row as WorkoutRoutine;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workout-routines', studentId] }),
  });

  return {
    routines: data?.routines ?? [],
    activeSession: data?.activeSession ?? null,
    loading,
    createRoutine,
    refetch,
  };
}
